// ============================================================
// FILE: src/components/Footer.jsx  →  BOTTOM FOOTER
// The dark footer on every page: logo, links, newsletter
// signup form, contact details and social icons.
// ============================================================

import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { WEB3FORMS_ACCESS_KEY, CONTACT_INFO, PRODUCT_CATEGORIES } from "../data/site";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    // Newsletter signups are emailed to export@evolvetrade.in via Web3Forms.
    const data = new FormData();
    data.append("access_key", WEB3FORMS_ACCESS_KEY);
    data.append("subject", "New newsletter signup — Evolve Website");
    data.append("from_name", "Evolve Trade & Logistics Website");
    data.append("email", email);
    data.append("type", "newsletter");
    const ok = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: data,
      headers: { Accept: "application/json" },
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        return r.ok && j.success === true;
      })
      .catch(() => false);
    if (ok) {
      toast.success("Subscribed to market insights.");
      setEmail("");
    } else {
      toast.error("Could not subscribe. Please try again.");
    }
    setLoading(false);
  };

  return (
    <footer className="relative border-t border-slate-900/10 dark:border-white/10" data-testid="footer">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/60 to-transparent" />
      <div className="container-x grid gap-12 py-16 lg:grid-cols-[1.4fr_1fr_1fr_1.4fr]">
        <div>
          <img src="/logo.png" alt="Evolve Trade & Logistics" className="h-16 w-auto object-contain" />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
            Connecting opportunities across global markets. Sourcing, export, import, procurement and logistics — one accountable partner.
          </p>
          <div className="mt-6 flex gap-3">
            {[
              { Icon: Linkedin, href: "https://www.linkedin.com/company/evolvetrade-and-logistics-private-limited/", label: "LinkedIn" },
              { Icon: Instagram, href: "https://www.instagram.com/evolve.trade.logistics/?utm_source=ig_web_button_share_sheet", label: "Instagram" },
            ].map(({ Icon, href, label }, i) => (
              <a
                key={i}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${i}`}
                className="glass flex h-9 w-9 items-center justify-center rounded-full transition-all duration-300 hover:scale-110 hover:border-teal-500/50 hover:text-teal-500"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="section-label mb-5">Company</p>
          <ul className="space-y-3 text-sm">
            {[["About Us", "/about"], ["Products", "/products"], ["Services", "/services"], ["Industries", "/industries"], ["Countries", "/countries"], ["Insights", "/insights"], ["Contact", "/contact"]].map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="text-muted-foreground transition-colors duration-200 hover:text-teal-500" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="section-label mb-5">Products</p>
          <ul className="space-y-3 text-sm">
            {PRODUCT_CATEGORIES.slice(0, 7).map((c) => (
              <li key={c}>
                <Link to={`/products?category=${encodeURIComponent(c)}`} className="text-muted-foreground transition-colors duration-200 hover:text-teal-500" data-testid={`footer-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  {c}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="section-label mb-5">Market Insights</p>
          <p className="text-sm text-muted-foreground">Monthly trade intelligence, commodity trends and regulation updates. No noise.</p>
          <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="newsletter-form">
            <input
              type="email"
              required
              aria-label="Email for newsletter"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Work email"
              className="field !py-2.5"
              data-testid="newsletter-email-input"
            />
            <button type="submit" disabled={loading} className="btn-primary !px-4 !py-2.5" data-testid="newsletter-submit-button" aria-label="Subscribe">
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <div className="mt-6 space-y-2.5 text-sm text-muted-foreground">
            <p className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-teal-500" /> {CONTACT_INFO.email}</p>
            <p className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-teal-500" /> {CONTACT_INFO.phone}</p>
            <p className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-teal-500" /> {CONTACT_INFO.phone2}</p>
            <p className="flex items-start gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-500" /> {CONTACT_INFO.address}</p>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-900/10 dark:border-white/10">
        <div className="container-x flex flex-col items-center justify-between gap-3 py-6 text-xs text-muted-foreground sm:flex-row">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Evolve Trade &amp; Logistics. All rights reserved.</p>
          <p>IEC • APEDA • FSSAI • GST • ISO 9001:2015 Registered</p>
        </div>
      </div>
    </footer>
  );
}
